import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { Button, IconButton, Tooltip } from '@mui/material';
import { memo, useEffect, useState } from 'react';

type CommandPaletteButtonProps = {
    onOpen: () => void;
};

/**
 * Button to open command palette with platform-specific hotkey display
 */
function CommandPaletteButton({ onOpen }: Readonly<CommandPaletteButtonProps>) {
    const [isMobile, setIsMobile] = useState(false);
    const [platform, setPlatform] = useState<'mac' | 'windows'>('mac');

    useEffect(() => {
        // Detect if mobile
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
        };

        // Detect platform
        const userAgent = navigator.userAgent.toLowerCase();

        if (userAgent.includes('mac')) {
            setPlatform('mac');
        } else {
            setPlatform('windows');
        }

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const hotkey = platform === 'mac' ? '⌘K' : 'Ctrl+K';

    if (isMobile) {
        return (
            <Tooltip title={`Search (${hotkey})`} placement="bottom">
                <IconButton
                    onClick={onOpen}
                    size="small"
                    className="border border-divider"
                >
                    <FuseSvgIcon size={20}>heroicons-outline:magnifying-glass</FuseSvgIcon>
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={`Search (${hotkey})`} placement="bottom">
            <Button
                onClick={onOpen}
                size="medium"
                variant="outlined"
                color="inherit"
                startIcon={<FuseSvgIcon size={20}>heroicons-outline:magnifying-glass</FuseSvgIcon>}
                className="border-divider normal-case text-sm px-10 py-2.5 min-w-0 hover:border-text-secondary"
            >
                Search
                {' '}
                <span className="ml-2.5 px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-semibold">
                    {hotkey}
                </span>
            </Button>
        </Tooltip>
    );
}

export default memo(CommandPaletteButton);
